import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Button,
  Chip,
  ChipProps,
  Collapse,
  createFilterOptions,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  SxProps,
  TextField,
  Theme,
  Tooltip,
  Typography,
  TypographyProps,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';

import { questionDifficultyOptions } from '../../../constants';
import {
  adminApproveQuestion,
  adminDeleteQuestion,
  adminGetQuestionById,
  adminGetQuestions,
  adminLoadMoreQuestions,
  adminUpdateQuestion,
  getQuestionState,
  IAdminUpdateQuestion,
  IQuestion,
  IQuestionDetailAnswer,
  TQuestionDifficulty,
} from '../../../store/QuestionSlice';
import { getTagsList, getTagsState } from '../../../store/TagsSlice';
import { getAuthState } from '../../../store/AuthSlice';
import { ISubTopic } from '../../../store/SubTopicSlice';
import { useAppSelector } from '../../../hooks';
import {
  checkUserPermission,
  getQuestionDifficultyChipColor,
} from '../../../helper';
import QuestionCard from '../ManageQuestions/components/QuestionCard';
import CustomGrayContainer from '../../../components/Containers/CustomGrayContainer';
import QuestionDetailsManager from '../ManageQuestions/components/ManageDetails/QuestionDetailsManager';
import AnswerManager from '../ManageQuestions/components/AnswerManagerV2';
import MarkingSchemeManager from '../ManageQuestions/components/ManageDetails/MarkingSchemeManager';
import DetailViewCard from '../../../components/Card/DetailViewCard';
import SimpleConfirmationAlert from '../../../components/Alert/SimpleConfirmationAlert';
import AddNewSubTopicDrawer from '../ManageQuestions/components/SubTopicManager/AddNewSubTopicDrawer';
import { getTenantsState } from '../../../store/V2/TenantsSlice';

interface IFilterData {
  limit: number;
  offset: number;
}

const defaultFilterData: IFilterData = {
  limit: 30,
  offset: 0,
};

const label: TypographyProps = {
  variant: 'subtitle2',
  fontWeight: 600,
  sx: { mb: '2px' },
};

const inputStyles: SxProps<Theme> = {
  backgroundColor: 'background.paper',
  borderRadius: '4px',
};

const filter = createFilterOptions();

const extractContents = (html: string) => {
  const span = document.createElement('span');

  span.innerHTML = html;

  const result = span.textContent || span.innerText;

  return result.substring(0, 100);
};

const Questions: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // State
  const [inputText, setInputText] = useState('');
  const [filterData, setFilterData] = useState<IFilterData>(defaultFilterData);
  const [questionDetails, setQuestionDetails] = useState('');
  const [questionSource, setQuestionSource] = useState('');
  const [markingSchemes, setMarkingSchemes] = useState('');
  const [questionMarks, setQuestionMarks] = useState('');
  const [questionTags, setQuestionTags] = useState<string[]>([]);
  const [answers, setAnswers] = useState<Array<IQuestionDetailAnswer>>([]);
  const [isToolTipOpen, setToolTipOpen] = useState(false);
  const [questionDifficulty, setQuestionDifficulty] = useState(
    questionDifficultyOptions[0]
  );

  const [questionTagsInputValue, setQuestionTagsInputValue] = useState<string>(
    ''
  );

  const [subTopics, setSubTopics] = useState<
    Array<{ subTopic: ISubTopic; paper: string }>
  >([]);

  const [
    deleteSubTopicItemAlertData,
    setDeleteSubTopicItemAlertData,
  ] = useState<{ open: boolean; value: string } | undefined>(undefined);

  const [
    isAddNewSubTopicDrawerVisible,
    setAddNewSubTopicDrawerVisibility,
  ] = useState(false);

  const [messageAlertData, setMessageAlertData] = useState<
    | {
        open: boolean;
        title: string;
        description?: string;
      }
    | undefined
  >(undefined);

  const [deleteQuestionAlertData, setDeleteQuestionAlertData] = useState<
    | {
        open: boolean;
        value: string;
      }
    | undefined
  >(undefined);

  // Store
  const questionState = useAppSelector(getQuestionState);
  const questionTagState = useAppSelector(getTagsState);
  const authState = useAppSelector(getAuthState);
  const tenantState = useAppSelector(getTenantsState);

  const { user: authUser } = authState;
  const { appTenant } = tenantState;

  // Permissions
  const questionCreatePermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      adminQuestion: ['create'],
    },
  });

  const questionUpdatePermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      adminQuestion: ['edit'],
    },
  });

  const questionDeletePermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      adminQuestion: ['delete'],
    },
  });

  const questionApprovePermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      adminQuestion: ['approve'],
    },
  });

  const questyStringParams = new URLSearchParams(location.search);
  const urlQuestionId = questyStringParams.get('questionId') || '';
  const questionList = [...questionState.list];
  const selectedQuestion =
    urlQuestionId && questionState.question
      ? questionState.question
      : undefined;

  const isSubmitDisabled =
    questionMarks.trim() === '' ||
    questionDetails.trim().length === 0 ||
    markingSchemes.trim().length === 0 ||
    answers.length === 0 ||
    !subTopics.length;

  let questionCreatedByname = '';
  let questionUpdatedByName = '';

  if (
    selectedQuestion &&
    !questionList.some(
      (questionListItem) => questionListItem._id === selectedQuestion?._id
    ) &&
    inputText.length < 5
  ) {
    questionList.push(selectedQuestion);
  }

  if (selectedQuestion && selectedQuestion.createdBy) {
    questionCreatedByname = selectedQuestion.createdBy.firstname || '';

    if (selectedQuestion.createdBy.lastname) {
      questionCreatedByname += ` ${selectedQuestion.createdBy.lastname}`;
    }
  }

  if (selectedQuestion && selectedQuestion.updatedBy) {
    questionUpdatedByName = selectedQuestion.updatedBy.firstname || '';

    if (selectedQuestion.updatedBy.lastname) {
      questionUpdatedByName += ` ${selectedQuestion.updatedBy.lastname}`;
    }
  }

  useEffect(() => {
    dispatch(adminGetQuestions(defaultFilterData));
  }, []);

  useEffect(() => {
    if (urlQuestionId) {
      dispatch(adminGetQuestionById(urlQuestionId));
    }
  }, [urlQuestionId, questionState.list]);

  useEffect(() => {
    if (!urlQuestionId && questionState.list && questionState.list.length) {
      onQuestionChange(questionState.list[0]);
    } else if (
      urlQuestionId &&
      questionState.list &&
      questionState.list.length === 1
    ) {
      onQuestionChange(questionState.list[0]);
    }
  }, [questionState.list]);

  useEffect(() => {
    if (selectedQuestion?._id === urlQuestionId) {
      setQuestionData(selectedQuestion);
    }
  }, [selectedQuestion]);

  const onQuestionChange = (question: IQuestion) => {
    if (!urlQuestionId) {
      navigate({
        search: `?questionId=${question._id}`,
      });
    } else if (urlQuestionId !== question._id) {
      questyStringParams.set('questionId', question._id);

      navigate({
        search: `?${questyStringParams.toString()}`,
      });
    }
  };

  const handleLoadMore = () => {
    const newFilterData = { ...filterData, offset: questionState.list.length };

    dispatch(adminLoadMoreQuestions(newFilterData));

    setFilterData(newFilterData);
  };

  const setQuestionData = (questionData: IQuestion) => {
    if (questionData) {
      setQuestionSource(questionData.source || '');
      setQuestionTags(questionData.tags || []);
      setSubTopics(
        (questionData.subTopics || []).filter((item) => item.subTopic)
      );

      setQuestionDetails(questionData.details || '');
      setMarkingSchemes(questionData.markingSchema || '');
      setAnswers(questionData.answers || []);
      setQuestionDifficulty(
        questionData.difficulty || questionDifficultyOptions[0]
      );

      setQuestionMarks(questionData.marks ? questionData.marks.toString() : '');
    }
  };

  const handleAddNewAnswerOption = () => {
    setAnswers((prevAnswers) => [
      ...prevAnswers,
      { order: prevAnswers.length, isCorrect: false, content: '' },
    ]);
  };

  const handleDeleteAnswerOption = (order: number) => {
    setAnswers((prevAnswers) => {
      const newAnswers = [...prevAnswers]
        .filter((answer) => answer.order !== order)
        .map((answer, index) => ({
          ...answer,
          order: index,
        }));

      return newAnswers;
    });
  };

  const handleCheckAnswerOption = (order: number) => {
    setAnswers((prevAnswers) => {
      const newAnswers = [...prevAnswers];
      newAnswers[order] = {
        ...prevAnswers[order],
        isCorrect: !prevAnswers[order].isCorrect,
      };

      return newAnswers;
    });
  };

  const handleUpdateAnswerContentOption = (order: number, content: string) => {
    setAnswers((prevAnswers) => {
      const newAnswers = [...prevAnswers];

      newAnswers[order] = {
        ...prevAnswers[order],
        content,
      };

      return newAnswers;
    });
  };

  const handleDragTopAnswerOption = (order: number) => {
    setAnswers((prevAnswers) => {
      const nextOrder = order - 1;
      const newAnswers = [...prevAnswers];
      const tempAnswer = { ...prevAnswers[order], order: nextOrder };

      newAnswers[order] = {
        ...prevAnswers[nextOrder],
        order,
      };

      newAnswers[nextOrder] = tempAnswer;

      return newAnswers;
    });
  };

  const handleDragDownAnswerOption = (order: number) => {
    setAnswers((prevAnswers) => {
      const nextOrder = order + 1;
      const newAnswers = [...prevAnswers];
      const tempAnswer = { ...prevAnswers[order], order: nextOrder };

      newAnswers[order] = {
        ...prevAnswers[nextOrder],
        order,
      };

      newAnswers[nextOrder] = tempAnswer;

      return newAnswers;
    });
  };

  const validateSubmit = () => {
    let valid = true;

    if (questionDetails.trim().length === 0) {
      valid = false;

      setMessageAlertData({
        open: true,
        title: 'Alert',
        description: 'Please enter question details',
      });
    } else if (markingSchemes.trim().length === 0) {
      valid = false;

      setMessageAlertData({
        open: true,
        title: 'Alert',
        description: 'Please enter marking schemes',
      });
    } else if (answers.length === 0) {
      setMessageAlertData({
        open: true,
        title: 'Alert',
        description: 'Please enter answers for the question',
      });
    } else if (answers.length > 0) {
      answers.forEach((answer) => {
        if (valid && answer.content.trim().length === 0) {
          valid = false;

          setMessageAlertData({
            open: true,
            title: 'Alert',
            description: 'Marking schemes cannot be empty',
          });
        }
      });
    }
    return valid;
  };

  const handleUpdateQuestion = () => {
    if (selectedQuestion && selectedQuestion._id) {
      const valid = validateSubmit();
      const apiSubTopics: Array<{
        subTopic: string;
        paper: string;
      }> = [];

      subTopics.forEach((subTopicItem) => {
        if (
          subTopicItem &&
          subTopicItem.subTopic &&
          subTopicItem.subTopic._id &&
          subTopicItem.paper
        ) {
          apiSubTopics.push({
            subTopic: subTopicItem.subTopic._id,
            paper: subTopicItem.paper,
          });
        }
      });

      if (valid) {
        const apiData: IAdminUpdateQuestion = {
          answers,
          id: selectedQuestion._id,
          difficulty: questionDifficulty,
          details: questionDetails,
          markingSchema: markingSchemes,
          subTopics: apiSubTopics,
        };

        if (questionSource.trim() !== '') {
          apiData.source = questionSource.trim();
        }

        if (questionMarks.trim() !== '') {
          apiData.marks = Number(questionMarks);
        }

        if (questionTags.length > 0) {
          apiData.tags = questionTags;
        }

        dispatch(adminUpdateQuestion(apiData));
      }
    }
  };

  const handleDeleteQuestion = () => {
    if (deleteQuestionAlertData) {
      dispatch(
        adminDeleteQuestion({
          id: deleteQuestionAlertData.value,
        })
      );

      setDeleteQuestionAlertData(undefined);
    }
  };

  const handleCopyQuestionId = (question: IQuestion) => {
    const textField = document.createElement('textarea');

    textField.innerText = question._id;
    document.body.appendChild(textField);

    textField.select();
    document.execCommand('copy');

    textField.remove();

    setToolTipOpen(true);

    setTimeout(() => {
      setToolTipOpen(false);
    }, 1500);
  };

  const handleApprove = (question: IQuestion) => {
    if (question.isApproved === true) {
      dispatch(
        adminApproveQuestion({
          id: question._id,
          isApproved: false,
        })
      );
    } else {
      dispatch(
        adminApproveQuestion({
          id: question._id,
          isApproved: true,
        })
      );
    }
  };

  return (
    <div>
      <div style={{ padding: '30px' }}>
        <Collapse in={Boolean(urlQuestionId)}>
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            sx={{ pb: '15px' }}
          >
            {questionUpdatePermission && selectedQuestion && (
              <Button
                color="primary"
                variant="contained"
                disabled={isSubmitDisabled}
                onClick={handleUpdateQuestion}
                style={{ marginRight: '10px' }}
              >
                Save
              </Button>
            )}
            {questionDeletePermission && selectedQuestion && (
              <Button
                variant="contained"
                color="error"
                sx={{ mr: '10px' }}
                onClick={() =>
                  setDeleteQuestionAlertData({
                    open: true,
                    value: selectedQuestion._id,
                  })
                }
              >
                Delete
              </Button>
            )}
            {questionApprovePermission && selectedQuestion && (
              <Button
                color={selectedQuestion.isApproved ? 'secondary' : 'info'}
                variant="contained"
                onClick={() => handleApprove(selectedQuestion)}
                style={{ marginRight: '10px' }}
              >
                {selectedQuestion.isApproved ? 'Unapprove' : 'Approve'}
              </Button>
            )}
            {/* <CustomGrayBoxIconButton
            onClick={() => setViewLoggerDrawerOpen(true)}
          >
            <InsertDriveFileIcon />
          </CustomGrayBoxIconButton> */}
          </Stack>
        </Collapse>
        <div>
          <Grid container spacing={2} sx={{ pb: '15px' }}>
            <Grid item xs={12} sm={12} md={4} lg={3}>
              <div style={{ padding: '0' }}>
                <FormControl fullWidth>
                  <TextField
                    size="small"
                    placeholder="Filter by Question Id"
                    value={inputText}
                    onChange={(event) => {
                      const inputValue = event.currentTarget.value;

                      setInputText(inputValue);

                      if (inputValue && inputValue.trim().length > 5) {
                        dispatch(
                          adminGetQuestions({
                            ...filterData,
                            questionId: inputValue,
                          })
                        );
                      } else {
                        dispatch(
                          adminGetQuestions({
                            ...filterData,
                          })
                        );
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <>
                          <SearchIcon />
                        </>
                      ),
                    }}
                  />
                </FormControl>
              </div>
              <div
                style={{ margin: '15px 0' }}
                className="question-list-item-container"
              >
                {questionList && questionList.length
                  ? questionList.map((question, index) => {
                      const isSelected = question._id === urlQuestionId;
                      const questionTitle = `Question ${(index + 1)
                        .toString()
                        .padStart(2, '0')}`;

                      let questionDescription = '';
                      if (question.details) {
                        questionDescription = extractContents(question.details);
                      }

                      const questionChips: Array<ChipProps> = [
                        {
                          color: getQuestionDifficultyChipColor(
                            question.difficulty
                          ),
                          label: question.difficulty.toLowerCase(),
                        },
                      ];

                      if (question.isApproved) {
                        questionChips.push({
                          color: 'info',
                          label: 'Approved',
                        });
                      }

                      return (
                        <QuestionCard
                          key={index}
                          isSelected={isSelected}
                          title={questionTitle}
                          description={questionDescription}
                          chips={questionChips}
                          onClick={() => onQuestionChange(question)}
                        />
                      );
                    })
                  : null}
                {questionState.count > questionState.list.length && (
                  <Stack direction="row" justifyContent="center">
                    <Button
                      disabled={questionState.status === 'loading'}
                      onClick={handleLoadMore}
                    >
                      Load More
                    </Button>
                  </Stack>
                )}
              </div>
            </Grid>
            {selectedQuestion ? (
              <Grid item xs={12} sm={12} md={8} lg={6}>
                <CustomGrayContainer>
                  <Stack
                    direction="row"
                    alignItems="center"
                    flexWrap="wrap"
                    style={{ marginBottom: '10px' }}
                  >
                    {selectedQuestion.deletedAt ? (
                      <Chip
                        color="error"
                        label={<b>Qurestion Deleted</b>}
                        style={{ margin: '0 5px 5px 0' }}
                      />
                    ) : null}
                    {selectedQuestion._id ? (
                      <Tooltip
                        open={isToolTipOpen}
                        title="ID Copied"
                        PopperProps={{
                          disablePortal: true,
                        }}
                      >
                        <Chip
                          style={{ margin: '0 5px 5px 0' }}
                          onClick={() => handleCopyQuestionId(selectedQuestion)}
                          label={
                            <span>
                              <b>Click to copy id:</b> {selectedQuestion._id}
                            </span>
                          }
                        />
                      </Tooltip>
                    ) : null}
                    {questionCreatedByname && (
                      <Chip
                        style={{ margin: '0 5px 5px 0' }}
                        label={
                          <span>
                            <b>Created by:</b> {questionCreatedByname}
                          </span>
                        }
                      />
                    )}
                    {questionUpdatedByName && (
                      <Chip
                        label={
                          <span>
                            <b>Last updated by: </b> {questionUpdatedByName}
                          </span>
                        }
                      />
                    )}
                  </Stack>
                  <QuestionDetailsManager
                    mode="edit"
                    details={questionDetails}
                    updateDetails={(html) => {
                      setQuestionDetails(html);
                    }}
                  />
                  <AnswerManager
                    answers={answers}
                    addNewOption={handleAddNewAnswerOption}
                    deleteOption={handleDeleteAnswerOption}
                    checkOption={handleCheckAnswerOption}
                    updateContentOption={handleUpdateAnswerContentOption}
                    dragOptionToTop={handleDragTopAnswerOption}
                    dragOptionToDown={handleDragDownAnswerOption}
                  />
                  <MarkingSchemeManager
                    mode="edit"
                    details={markingSchemes}
                    updateDetails={(html) => {
                      setMarkingSchemes(html);
                    }}
                  />
                </CustomGrayContainer>
              </Grid>
            ) : null}
            {selectedQuestion ? (
              <Grid item xs={12} sm={12} md={12} lg={3}>
                <CustomGrayContainer>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4} md={4} lg={12}>
                      <FormControl fullWidth>
                        <Typography {...label}>
                          Question Difficulty Level *
                        </Typography>
                        <Select
                          size="small"
                          sx={inputStyles}
                          value={questionDifficulty}
                          disabled={questionState.status === 'loading'}
                          onChange={(event) => {
                            const option = event.target
                              .value as TQuestionDifficulty;
                            setQuestionDifficulty(option);
                          }}
                        >
                          {questionDifficultyOptions.map(
                            (difficultyOption, index) => (
                              <MenuItem key={index} value={difficultyOption}>
                                {difficultyOption}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4} md={4} lg={12}>
                      <FormControl fullWidth>
                        <Typography {...label}>Question Source</Typography>
                        <TextField
                          size="small"
                          sx={inputStyles}
                          value={questionSource}
                          placeholder="Enter Source"
                          disabled={questionState.status === 'loading'}
                          onChange={(event) =>
                            setQuestionSource(event.currentTarget.value)
                          }
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4} md={4} lg={12}>
                      <FormControl fullWidth>
                        <Typography {...label}>Marks *</Typography>
                        <TextField
                          size="small"
                          type="number"
                          InputProps={{
                            inputProps: { max: 100, maxLength: 100 },
                          }}
                          sx={inputStyles}
                          value={questionMarks}
                          placeholder="Add Marks"
                          disabled={questionState.status === 'loading'}
                          onChange={(event) => {
                            if (Number(event.currentTarget.value) > 100) {
                              setQuestionMarks('100');
                            } else {
                              setQuestionMarks(event.currentTarget.value);
                            }
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <FormControl fullWidth>
                        <Typography {...label}>Tags</Typography>
                        <Autocomplete
                          multiple
                          handleHomeEndKeys
                          size="small"
                          sx={inputStyles}
                          disabled={questionState.status === 'loading'}
                          inputValue={questionTagsInputValue}
                          onInputChange={(event, value) => {
                            if (event) {
                              const _value = value.toLowerCase();
                              setQuestionTagsInputValue(_value);
                              if (_value.length > 0) {
                                dispatch(
                                  getTagsList({
                                    offset: 0,
                                    limit: 30,
                                    name: _value,
                                  })
                                );
                              }
                            }
                          }}
                          value={questionTags.map((qt) => ({
                            label: qt,
                            value: qt,
                          }))}
                          options={[
                            ...questionTagState.list.map((questionTag) => ({
                              label: questionTag.name,
                              value: questionTag.name,
                            })),
                          ]}
                          onChange={(event, values) => {
                            setQuestionTags(values.map((tag) => tag.value));
                            setQuestionTagsInputValue('');
                          }}
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          filterOptions={(options, params: any) => {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const filtered: any = filter(options, params);

                            const { inputValue } = params;
                            const formatedInputValue = inputValue.trim();
                            // Suggest the creation of a new value
                            const isExisting = options.some(
                              (option) => formatedInputValue === option.value
                            );
                            if (formatedInputValue !== '' && !isExisting) {
                              if (!questionTags.includes(formatedInputValue)) {
                                filtered.push({
                                  value: formatedInputValue,
                                  label: `Add "${formatedInputValue}"`,
                                });
                              }
                            }

                            return filtered;
                          }}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="Select Tags" />
                          )}
                        />
                      </FormControl>
                      <div style={{ paddingBottom: '15px' }}>
                        {subTopics.length ? (
                          <div style={{ paddingTop: '15px' }}>
                            <Typography
                              fontWeight={500}
                              textAlign="center"
                              sx={{ mb: '15px' }}
                            >
                              DUPLICATED QUESTIONS
                            </Typography>
                            <div>
                              {subTopics.map((subtopicListItem, index) => {
                                const subTopicName =
                                  subtopicListItem &&
                                  subtopicListItem.subTopic &&
                                  subtopicListItem.subTopic.name
                                    ? subtopicListItem.subTopic.name
                                    : '';

                                const paperName =
                                  subtopicListItem && subtopicListItem.paper
                                    ? subtopicListItem.paper
                                    : '';

                                return (
                                  <Grid
                                    key={index}
                                    container
                                    spacing={2}
                                    alignItems="center"
                                  >
                                    <Grid item xs={2} sm={11} md={11} lg={10}>
                                      <DetailViewCard
                                        title="Sub Topic"
                                        description={`${subTopicName} - ${paperName}`}
                                        titleProps={{
                                          variant: 'caption',
                                          fontWeight: 600,
                                        }}
                                        descriptionProps={{
                                          variant: 'subtitle2',
                                          className: 'ellipsis-text',
                                        }}
                                        containerProps={{
                                          sx: {
                                            backgroundColor: 'background.paper',
                                            color: 'text.primary',
                                          },
                                        }}
                                      />
                                    </Grid>
                                    <Grid item xs={2} sm={1} md={1} lg={2}>
                                      <IconButton
                                        color="secondary"
                                        onClick={() => {
                                          setDeleteSubTopicItemAlertData({
                                            open: true,
                                            value:
                                              subtopicListItem.subTopic._id,
                                          });
                                        }}
                                      >
                                        <CancelIcon />
                                      </IconButton>
                                    </Grid>
                                  </Grid>
                                );
                              })}
                            </div>
                          </div>
                        ) : null}
                        {questionCreatePermission && (
                          <Stack
                            direction="row"
                            justifyContent="center"
                            sx={{ pt: '15px' }}
                          >
                            <Button
                              disabled={questionState.status === 'loading'}
                              color="primary"
                              startIcon={<AddIcon />}
                              onClick={() => {
                                setAddNewSubTopicDrawerVisibility(true);
                              }}
                            >
                              New Subject
                            </Button>
                          </Stack>
                        )}
                      </div>
                    </Grid>
                  </Grid>
                </CustomGrayContainer>
              </Grid>
            ) : null}
          </Grid>
        </div>
      </div>
      {/* Modals */}
      {deleteSubTopicItemAlertData &&
        deleteSubTopicItemAlertData.open &&
        deleteSubTopicItemAlertData.value && (
          <SimpleConfirmationAlert
            open={deleteSubTopicItemAlertData.open}
            onClose={() => setDeleteSubTopicItemAlertData(undefined)}
            title="Are you sure"
            description="Do you want to delete this item?"
            cancelButtonProps={{
              onClick: () => {
                setDeleteSubTopicItemAlertData(undefined);
              },
              label: 'Cancel',
            }}
            okButtonprops={{
              onClick: () => {
                setSubTopics((prevSubTopics) =>
                  prevSubTopics.filter(
                    (questionSubTopic) =>
                      questionSubTopic.subTopic._id !==
                      deleteSubTopicItemAlertData.value
                  )
                );
                setDeleteSubTopicItemAlertData(undefined);
              },
              color: 'error',
              label: 'Delete',
            }}
          />
        )}
      {isAddNewSubTopicDrawerVisible && (
        <AddNewSubTopicDrawer
          open={isAddNewSubTopicDrawerVisible}
          onClose={() => setAddNewSubTopicDrawerVisibility(false)}
          addSubTopic={(questionSubTopics) => {
            const newSubTopics = [...subTopics];

            if (questionSubTopics && questionSubTopics.length) {
              questionSubTopics.forEach((questionSubTopic) => {
                const isSubTopicExist = newSubTopics.some(
                  (s) =>
                    s.subTopic._id === questionSubTopic.subTopic._id &&
                    s.paper === questionSubTopic.paper
                );

                if (!isSubTopicExist) {
                  newSubTopics.push(questionSubTopic);
                }
              });
            }

            setSubTopics(newSubTopics);
          }}
        />
      )}

      {messageAlertData && messageAlertData.open && (
        <SimpleConfirmationAlert
          open={messageAlertData.open}
          onClose={() => setMessageAlertData(undefined)}
          title={messageAlertData.title || ''}
          description={messageAlertData.description}
          cancelButtonProps={{
            label: 'Close',
            color: 'info',
            onClick: () => setMessageAlertData(undefined),
          }}
        />
      )}
      {deleteQuestionAlertData &&
        deleteQuestionAlertData.open &&
        deleteQuestionAlertData.value && (
          <SimpleConfirmationAlert
            open={deleteQuestionAlertData?.open}
            onClose={() => setDeleteQuestionAlertData(undefined)}
            title="Are you Sure?"
            description="Do you want to delete this question?"
            cancelButtonProps={{
              label: 'Close',
              color: 'info',
              onClick: () => setDeleteQuestionAlertData(undefined),
            }}
            okButtonprops={{
              label: 'Confirm',
              color: 'info',
              onClick: handleDeleteQuestion,
            }}
          />
        )}
    </div>
  );
};

export default Questions;
